import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  ArrowLeft,
  ArrowRight,
  Clock,
  BookOpen,
  CheckCircle2,
  ExternalLink,
  Printer,
  Video,
  Volume2,
  FileText,
  Download,
  Brain,
  Layers,
  HelpCircle,
  Presentation,
  Mic,
  MessageSquare,
  FileCheck,
  LayoutGrid,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { ModuleCompletionCard } from '@/components/pathways/ModuleCompletionCard';
// Content Studio viewers for embedded content
import {
  PodcastPlayer,
  MindMapViewer,
  FlashcardsViewer,
  QuizViewer,
  SlidesViewer,
  StudyGuideViewer,
  AnalysisViewer,
  FAQViewer,
  SummaryViewer,
  TimelineViewer,
  BriefingDocViewer,
} from '@/components/ContentStudio';
import type { StudioContentType } from '@/components/pathways/studioContentFormatter';

interface ModuleConfig {
  content_source?: 'url' | 'upload' | 'text' | 'video' | 'audio' | 'studio';
  content_text?: string;
  estimated_reading_time?: number;
  video_url?: string;
  audio_url?: string;
  file_url?: string; // Original uploaded file URL
  // Content Studio fields
  studio_content_type?: StudioContentType;
  studio_generation_id?: string;
  studio_generated_content?: unknown;
  studio_title?: string;
}

export default function ReadingModulePage() {
  const { moduleId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const pathwayId = searchParams.get('pathway');
  
  const [currentPage, setCurrentPage] = useState(1);
  const [isCompleted, setIsCompleted] = useState(false);
  const [completionData, setCompletionData] = useState<{
    nextModule?: any; totalModules: number; modulesCompleted: number; isPathwayComplete: boolean; targetPathwayId: string;
  } | null>(null);

  // Fetch module details
  const { data: module, isLoading } = useQuery({
    queryKey: ['reading-module', moduleId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pathway_modules')
        .select(`
          *,
          learning_pathways (id, name)
        `)
        .eq('id', moduleId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!moduleId
  });

  // Fetch module progress - use maybeSingle to avoid 406 when no rows exist
  const { data: moduleProgress } = useQuery({
    queryKey: ['module-progress', moduleId, profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('module_progress')
        .select('*')
        .eq('module_id', moduleId!)
        .eq('user_id', profile!.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!moduleId && !!profile?.id
  });

  // Update completion status based on fetched progress
  useEffect(() => {
    if (moduleProgress?.status === 'completed') {
      setIsCompleted(true);
    }
  }, [moduleProgress]);

  // Mark as complete mutation with progression logic
  const completeModuleMutation = useMutation({
    mutationFn: async () => {
      if (!profile?.id || !moduleId) throw new Error('Missing user or module');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated with Supabase. Please refresh and try again.');

      // First, find the enrollment
      const targetPathwayId = pathwayId || module?.pathway_id;
      if (!targetPathwayId) throw new Error('No pathway found');

      // Use maybeSingle to avoid 406 errors
      const { data: enrollment, error: enrollError } = await supabase
        .from('user_pathway_enrollments')
        .select('id, modules_completed')
        .eq('user_id', profile.id)
        .eq('pathway_id', targetPathwayId)
        .maybeSingle();

      if (enrollError) throw enrollError;

      // Create enrollment if doesn't exist
      let enrollmentId = enrollment?.id;
      let currentModulesCompleted = enrollment?.modules_completed || 0;
      
      if (!enrollmentId) {
        const { data: newEnrollment, error: createError } = await supabase
          .from('user_pathway_enrollments')
          .insert({
            user_id: profile.id,
            pathway_id: targetPathwayId,
            status: 'in_progress'
          })
          .select()
          .single();

        if (createError) throw createError;
        enrollmentId = newEnrollment.id;
        currentModulesCompleted = 0;

        // Initialize module_progress rows for all modules in this pathway
        const { data: pathwayModules } = await supabase
          .from('pathway_modules')
          .select('id, sequence_order')
          .eq('pathway_id', targetPathwayId)
          .order('sequence_order');

        if (pathwayModules && pathwayModules.length > 0) {
          const progressRows = pathwayModules.map((mod, idx) => ({
            user_id: profile.id,
            enrollment_id: enrollmentId!,
            module_id: mod.id,
            status: idx === 0 ? 'unlocked' : 'locked'
          }));

          await supabase.from('module_progress').insert(progressRows);
        }
      }

      // Upsert module progress - use enrollment_id,module_id (matching DB constraint)
      const { error } = await supabase
        .from('module_progress')
        .upsert({
          user_id: profile.id,
          module_id: moduleId,
          enrollment_id: enrollmentId,
          status: 'completed',
          completed_at: new Date().toISOString(),
          best_score: 100, // Reading is pass/fail
          attempts: 1
        }, {
          onConflict: 'enrollment_id,module_id'
        });

      if (error) throw error;

      // === PROGRESSION LOGIC ===
      // Get all modules in order to find the next one (include details for completion card)
      const { data: allModules } = await supabase
        .from('pathway_modules')
        .select('id, sequence_order, title, module_type, estimated_minutes, simulation_agent_id')
        .eq('pathway_id', targetPathwayId)
        .order('sequence_order');

      let nextModuleInfo = null;
      let totalModules = 0;
      let newModulesCompleted = currentModulesCompleted + 1;
      let isPathwayComplete = false;

      if (allModules && allModules.length > 0) {
        totalModules = allModules.length;
        const currentModuleIndex = allModules.findIndex(m => m.id === moduleId);
        const nextModule = allModules[currentModuleIndex + 1];
        isPathwayComplete = !nextModule;

        if (nextModule) {
          nextModuleInfo = nextModule;
        }

        await supabase
          .from('user_pathway_enrollments')
          .update({
            modules_completed: newModulesCompleted,
            current_module_id: nextModule?.id || null,
            status: isPathwayComplete ? 'completed' : 'in_progress',
            completed_at: isPathwayComplete ? new Date().toISOString() : null
          })
          .eq('id', enrollmentId);

        // Unlock the next module if it exists
        if (nextModule) {
          await supabase
            .from('module_progress')
            .update({ status: 'unlocked' })
            .eq('enrollment_id', enrollmentId)
            .eq('module_id', nextModule.id);
        }

      }

      return { enrollmentId, targetPathwayId, nextModule: nextModuleInfo, totalModules, modulesCompleted: newModulesCompleted, isPathwayComplete };
    },
    onSuccess: (result) => {
      setIsCompleted(true);
      setCompletionData(result || null);

      // Invalidate all relevant queries for UI refresh
      queryClient.invalidateQueries({ queryKey: ['module-progress', moduleId, profile?.id] });
      queryClient.invalidateQueries({ queryKey: ['module-progress', result?.enrollmentId] });
      queryClient.invalidateQueries({ queryKey: ['pathway-enrollment', result?.targetPathwayId, profile?.id] });
      queryClient.invalidateQueries({ queryKey: ['pathway-progress', result?.targetPathwayId, profile?.id] });
      queryClient.invalidateQueries({ queryKey: ['user-enrollments', profile?.id] });
      queryClient.invalidateQueries({ queryKey: ['pathway-modules'] });

      toast.success('Reading completed! Great progress!');
    },
    onError: (error) => {
      toast.error('Failed to mark as complete: ' + error.message);
    }
  });

  // Parse content into pages
  const parseContentIntoPages = (htmlContent: string, wordsPerPage = 500) => {
    if (!htmlContent) return [''];
    
    // Split by paragraphs or major sections
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    const blocks = tempDiv.querySelectorAll('p, h1, h2, h3, h4, h5, h6, ul, ol, blockquote, pre');
    
    if (blocks.length === 0) {
      // If no block elements, just return as single page
      return [htmlContent];
    }

    const pages: string[] = [];
    let currentPageContent = '';
    let currentWordCount = 0;

    blocks.forEach((block) => {
      const blockWordCount = block.textContent?.split(/\s+/).length || 0;
      
      if (currentWordCount + blockWordCount > wordsPerPage && currentPageContent) {
        pages.push(currentPageContent);
        currentPageContent = block.outerHTML;
        currentWordCount = blockWordCount;
      } else {
        currentPageContent += block.outerHTML;
        currentWordCount += blockWordCount;
      }
    });

    if (currentPageContent) {
      pages.push(currentPageContent);
    }

    return pages.length > 0 ? pages : [htmlContent];
  };

  const moduleConfig = module?.module_config as ModuleConfig | null;
  const contentSource = moduleConfig?.content_source || 'url';
  const contentText = moduleConfig?.content_text || '';
  const contentUrl = module?.content_url;

  const videoUrl = moduleConfig?.video_url || '';
  const audioUrl = moduleConfig?.audio_url || '';
  const fileUrl = moduleConfig?.file_url || contentUrl || ''; // The uploaded file URL

  const pages = parseContentIntoPages(contentText);
  
  // Check if this is a PDF file
  const isPdf = fileUrl?.toLowerCase()?.endsWith('.pdf') || 
    contentUrl?.toLowerCase()?.endsWith('.pdf') ||
    (contentSource === 'upload' && !contentText && fileUrl);
  const totalPages = pages.length;
  const hasDirectContent = contentSource === 'text' && contentText;
  const estimatedTime = moduleConfig?.estimated_reading_time || module?.estimated_minutes || 10;

  // Helper to extract YouTube/Vimeo embed URL
  const getEmbedUrl = (url: string): string => {
    if (!url) return '';
    
    // YouTube
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&?\s]+)/);
    if (ytMatch) {
      return `https://www.youtube.com/embed/${ytMatch[1]}`;
    }
    
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }
    
    // Direct video URL
    return url;
  };

  const isDirectVideo = (url: string): boolean => {
    return /\.(mp4|webm|ogg)$/i.test(url);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleComplete = () => {
    completeModuleMutation.mutate();
  };

  const handleContinue = () => {
    if (pathwayId) {
      navigate(`/learning/pathway/${pathwayId}`);
    } else if (module?.pathway_id) {
      navigate(`/learning/pathway/${module.pathway_id}`);
    } else {
      navigate('/learning');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="h-12 w-12 mx-auto text-primary animate-pulse" />
          <p className="mt-4 text-muted-foreground">Loading reading material...</p>
        </div>
      </div>
    );
  }

  if (!module) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">Module Not Found</h2>
            <p className="text-muted-foreground mb-4">
              The reading material you're looking for doesn't exist.
            </p>
            <Button onClick={() => navigate(-1)}>Go Back</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // =====================================================
  // CONTENT STUDIO CONTENT - Embedded viewers
  // =====================================================
  const studioContentType = moduleConfig?.studio_content_type;
  const studioContent = moduleConfig?.studio_generated_content as any;
  const studioTitle = moduleConfig?.studio_title || module.title;

  if (contentSource === 'studio' && studioContentType && studioContent) {
    // Helper function to get content type icon and label
    const getContentTypeInfo = (type: StudioContentType) => {
      switch (type) {
        case 'podcast':
        case 'video-podcast':
          return { icon: Mic, label: 'Podcast', color: 'bg-purple-100 text-purple-700' };
        case 'mind-map':
          return { icon: Brain, label: 'Mind Map', color: 'bg-pink-100 text-pink-700' };
        case 'flashcards':
          return { icon: Layers, label: 'Flashcards', color: 'bg-cyan-100 text-cyan-700' };
        case 'quiz':
          return { icon: HelpCircle, label: 'Quiz', color: 'bg-orange-100 text-orange-700' };
        case 'slides':
          return { icon: Presentation, label: 'Slides', color: 'bg-blue-100 text-blue-700' };
        case 'study-guide':
          return { icon: BookOpen, label: 'Study Guide', color: 'bg-emerald-100 text-emerald-700' };
        case 'summary':
          return { icon: FileCheck, label: 'Summary', color: 'bg-green-100 text-green-700' };
        case 'faq':
          return { icon: MessageSquare, label: 'FAQ', color: 'bg-amber-100 text-amber-700' };
        case 'timeline':
          return { icon: Clock, label: 'Timeline', color: 'bg-indigo-100 text-indigo-700' };
        case 'briefing-doc':
          return { icon: LayoutGrid, label: 'Briefing Doc', color: 'bg-teal-100 text-teal-700' };
        case 'analyze':
          return { icon: Search, label: 'Analysis', color: 'bg-slate-100 text-slate-700' };
        default:
          return { icon: FileText, label: 'Content', color: 'bg-gray-100 text-gray-700' };
      }
    };

    const typeInfo = getContentTypeInfo(studioContentType);
    const TypeIcon = typeInfo.icon;

    // Render appropriate viewer based on content type
    const renderStudioViewer = () => {
      switch (studioContentType) {
        case 'podcast':
        case 'video-podcast': {
          // Build podcast result structure for PodcastPlayer
          const podcastResult = {
            script: studioContent.script || studioContent,
            audioSegments: studioContent.audioSegments || [],
            isVideoPodcast: studioContentType === 'video-podcast',
            videoPodcast: studioContent.videoPodcast,
            combinedAudioUrl: moduleConfig?.audio_url || studioContent.combinedAudioUrl,
          };
          return <PodcastPlayer podcast={podcastResult} className="w-full" />;
        }
        
        case 'mind-map': {
          // Build mind map structure for MindMapViewer
          const mindMap = {
            title: studioContent.title || studioTitle,
            centralTopic: studioContent.centralTopic || studioContent.central_topic || studioTitle,
            nodes: studioContent.nodes || studioContent.branches || [],
            connections: studioContent.connections || [],
          };
          return <MindMapViewer mindMap={mindMap} className="w-full" />;
        }
        
        case 'flashcards': {
          // Build flashcard deck for FlashcardsViewer
          const deck = {
            title: studioContent.title || studioTitle,
            description: studioContent.description || '',
            cards: studioContent.cards || studioContent.flashcards || [],
            categories: studioContent.categories || [],
          };
          return <FlashcardsViewer deck={deck} className="w-full" />;
        }
        
        case 'quiz': {
          // Build quiz for QuizViewer
          const quiz = {
            title: studioContent.title || studioTitle,
            description: studioContent.description || '',
            questions: studioContent.questions || [],
            passingScore: studioContent.passingScore || 70,
            timeLimit: studioContent.timeLimit,
          };
          return <QuizViewer quiz={quiz} className="w-full" />;
        }
        
        case 'slides': {
          // Build slides for SlidesViewer
          const slides = {
            title: studioContent.title || studioTitle,
            slides: studioContent.slides || [],
            theme: studioContent.theme || 'professional',
          };
          return <SlidesViewer slides={slides} className="w-full" />;
        }
        
        case 'study-guide': {
          // Build study guide for StudyGuideViewer
          const studyGuide = {
            title: studioContent.title || studioTitle,
            sections: studioContent.sections || [],
            keyTakeaways: studioContent.keyTakeaways || studioContent.key_takeaways || [],
            reviewQuestions: studioContent.reviewQuestions || studioContent.review_questions || [],
          };
          return <StudyGuideViewer studyGuide={studyGuide} className="w-full" />;
        }
        
        case 'summary': {
          const summaryData = {
            title: studioContent.title || studioTitle,
            executiveSummary: studioContent.executiveSummary || studioContent.executive_summary || '',
            keyPoints: studioContent.keyPoints || studioContent.key_points || [],
            sections: studioContent.sections || [],
            wordCount: studioContent.wordCount || studioContent.word_count || 0,
          };
          return <SummaryViewer summary={summaryData} className="w-full" />;
        }

        case 'faq': {
          const faqData = {
            title: studioContent.title || studioTitle,
            description: studioContent.description || '',
            items: studioContent.items || studioContent.questions || [],
            categories: studioContent.categories || [],
          };
          return <FAQViewer faq={faqData} className="w-full" />;
        }

        case 'timeline': {
          const timelineData = {
            title: studioContent.title || studioTitle,
            description: studioContent.description || '',
            events: studioContent.events || studioContent.timeline || [],
          };
          return <TimelineViewer timeline={timelineData} className="w-full" />;
        }

        case 'briefing-doc': {
          const briefingData = {
            title: studioContent.title || studioTitle,
            date: studioContent.date || new Date().toISOString().split('T')[0],
            preparedFor: studioContent.preparedFor || studioContent.prepared_for,
            executiveSummary: studioContent.executiveSummary || studioContent.executive_summary || '',
            background: studioContent.background || '',
            keyFindings: studioContent.keyFindings || studioContent.key_findings || [],
            recommendations: studioContent.recommendations || [],
            nextSteps: studioContent.nextSteps || studioContent.next_steps,
            appendix: studioContent.appendix,
          };
          return <BriefingDocViewer briefingDoc={briefingData} className="w-full" />;
        }

        case 'analyze': {
          const analysisData = {
            title: studioContent.title || studioTitle,
            summary: studioContent.summary || '',
            keyTopics: studioContent.keyTopics || studioContent.key_topics || [],
            mainPoints: studioContent.mainPoints || studioContent.main_points || [],
            terminology: studioContent.terminology || [],
            suggestedQuestions: studioContent.suggestedQuestions || studioContent.suggested_questions || [],
            estimatedReadTime: studioContent.estimatedReadTime || studioContent.estimated_read_time || '',
            complexity: studioContent.complexity || 'intermediate',
          };
          return <AnalysisViewer analysis={analysisData} className="w-full" />;
        }

        // Fallback for unknown types
        default: {
          const textContent = moduleConfig?.content_text || '';
          return (
            <div
              className="prose prose-sm sm:prose max-w-none dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: textContent }}
            />
          );
        }
      }
    };

    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-5xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Pathway
            </Button>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <Badge variant="outline" className={`gap-1 ${typeInfo.color}`}>
                  <TypeIcon className="h-3 w-3" />
                  {typeInfo.label}
                </Badge>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {estimatedTime} min
                </span>
              </div>
              <CardTitle className="text-2xl">{module.title}</CardTitle>
              {module.description && (
                <p className="text-muted-foreground mt-2">{module.description}</p>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Render the appropriate Content Studio viewer */}
              {renderStudioViewer()}

              {/* Completion section */}
              <div className="border-t pt-6 flex items-center justify-between">
                <Button variant="outline" onClick={() => navigate(-1)}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                
                {isCompleted ? (
                  <div className="flex items-center gap-2 text-primary">
                    <CheckCircle2 className="h-5 w-5" />
                    <span className="font-medium">Completed</span>
                  </div>
                ) : (
                  <Button onClick={handleComplete} disabled={completeModuleMutation.isPending}>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    {completeModuleMutation.isPending ? 'Saving...' : 'Mark as Complete'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Video content
  if (contentSource === 'video' && videoUrl) {
    const embedUrl = getEmbedUrl(videoUrl);
    const isDirectFile = isDirectVideo(videoUrl);

    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Pathway
            </Button>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <Badge variant="outline" className="gap-1">
                  <Video className="h-3 w-3" />
                  Video
                </Badge>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {estimatedTime} min
                </span>
              </div>
              <CardTitle className="text-2xl">{module.title}</CardTitle>
              {module.description && (
                <p className="text-muted-foreground mt-2">{module.description}</p>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="aspect-video rounded-lg overflow-hidden bg-black">
                {isDirectFile ? (
                  <video 
                    controls 
                    className="w-full h-full"
                    preload="metadata"
                  >
                    <source src={videoUrl} type="video/mp4" />
                    Your browser does not support the video element.
                  </video>
                ) : (
                  <iframe
                    src={embedUrl}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={module.title}
                  />
                )}
              </div>

              <div className="border-t pt-6 flex items-center justify-between">
                <Button variant="outline" onClick={() => navigate(-1)}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                
                {isCompleted ? (
                  <div className="flex items-center gap-2 text-primary">
                    <CheckCircle2 className="h-5 w-5" />
                    <span className="font-medium">Completed</span>
                  </div>
                ) : (
                  <Button onClick={handleComplete} disabled={completeModuleMutation.isPending}>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    {completeModuleMutation.isPending ? 'Saving...' : 'Mark as Complete'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Audio content
  if (contentSource === 'audio' && audioUrl) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Pathway
            </Button>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <Badge variant="outline" className="gap-1">
                  <Volume2 className="h-3 w-3" />
                  Audio
                </Badge>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {estimatedTime} min
                </span>
              </div>
              <CardTitle className="text-2xl">{module.title}</CardTitle>
              {module.description && (
                <p className="text-muted-foreground mt-2">{module.description}</p>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-8 bg-muted/50 rounded-lg">
                <div className="flex flex-col items-center gap-6">
                  <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
                    <Volume2 className="h-12 w-12 text-primary" />
                  </div>
                  <audio 
                    controls 
                    className="w-full max-w-md"
                    preload="metadata"
                  >
                    <source src={audioUrl} />
                    Your browser does not support the audio element.
                  </audio>
                </div>
              </div>

              <div className="border-t pt-6 flex items-center justify-between">
                <Button variant="outline" onClick={() => navigate(-1)}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                
                {isCompleted ? (
                  <div className="flex items-center gap-2 text-primary">
                    <CheckCircle2 className="h-5 w-5" />
                    <span className="font-medium">Completed</span>
                  </div>
                ) : (
                  <Button onClick={handleComplete} disabled={completeModuleMutation.isPending}>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    {completeModuleMutation.isPending ? 'Saving...' : 'Mark as Complete'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // UPLOADED PDF: Show inline PDF viewer + extracted text
  if (contentSource === 'upload' && (isPdf || fileUrl)) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-5xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Pathway
            </Button>
            <div className="flex items-center gap-2">
              {fileUrl && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => window.open(fileUrl, '_blank')}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download
                </Button>
              )}
            </div>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <Badge variant="outline" className="gap-1">
                  <FileText className="h-3 w-3" />
                  Document
                </Badge>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {estimatedTime} min read
                </span>
              </div>
              <CardTitle className="text-2xl">{module.title}</CardTitle>
              {module.description && (
                <p className="text-muted-foreground mt-2">{module.description}</p>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              {/* PDF Viewer - if we have a file URL */}
              {fileUrl && isPdf && (
                <div className="aspect-[3/4] w-full rounded-lg overflow-hidden border bg-muted">
                  <iframe 
                    src={`${fileUrl}#toolbar=1&navpanes=0`}
                    className="w-full h-full"
                    title={module.title}
                  />
                </div>
              )}
              
              {/* Extracted text content - show if we have it */}
              {contentText && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                      Extracted Content
                    </h3>
                    <Button variant="ghost" size="sm" onClick={handlePrint}>
                      <Printer className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {/* Progress bar for multi-page content */}
                  {totalPages > 1 && (
                    <div className="mb-4">
                      <Progress value={(currentPage / totalPages) * 100} className="h-2" />
                      <p className="text-xs text-muted-foreground mt-1 text-center">
                        Page {currentPage} of {totalPages}
                      </p>
                    </div>
                  )}
                  
                  <div 
                    className="prose prose-sm sm:prose max-w-none dark:prose-invert bg-muted/30 p-6 rounded-lg border"
                    dangerouslySetInnerHTML={{ __html: pages[currentPage - 1] || '' }}
                  />
                  
                  {/* Pagination for extracted text */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between pt-4">
                      <Button
                        variant="outline"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  )}
                </div>
              )}
              
              {/* No content extracted message */}
              {!contentText && !isPdf && (
                <div className="p-8 bg-muted/50 rounded-lg text-center">
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">
                    The document content could not be extracted. You can download the original file below.
                  </p>
                  {fileUrl && (
                    <Button onClick={() => window.open(fileUrl, '_blank')} className="gap-2">
                      <Download className="h-4 w-4" />
                      Download Document
                    </Button>
                  )}
                </div>
              )}

              {/* Completion section */}
              <div className="border-t pt-6 flex items-center justify-between">
                <Button variant="outline" onClick={() => navigate(-1)}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                
                {isCompleted ? (
                  <div className="flex items-center gap-2 text-primary">
                    <CheckCircle2 className="h-5 w-5" />
                    <span className="font-medium">Completed</span>
                  </div>
                ) : (
                  <Button onClick={handleComplete} disabled={completeModuleMutation.isPending}>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    {completeModuleMutation.isPending ? 'Saving...' : 'Mark as Complete'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // If content is URL-based, redirect to external link
  if (contentSource === 'url' && contentUrl) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Pathway
            </Button>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <Badge variant="outline">Reading Material</Badge>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {estimatedTime} min
                </span>
              </div>
              <CardTitle className="text-2xl">{module.title}</CardTitle>
              {module.description && (
                <p className="text-muted-foreground mt-2">{module.description}</p>
              )}
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-6 bg-muted/50 rounded-lg text-center">
                <ExternalLink className="h-12 w-12 mx-auto text-primary mb-4" />
                <p className="text-muted-foreground mb-4">
                  This reading material is hosted externally. Click below to open it in a new tab.
                </p>
                <Button 
                  onClick={() => window.open(contentUrl, '_blank')} 
                  className="gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open Reading Material
                </Button>
              </div>

              <div className="border-t pt-6 flex items-center justify-between">
                <Button variant="outline" onClick={() => navigate(-1)}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                
                {isCompleted ? (
                  <div className="flex items-center gap-2 text-primary">
                    <CheckCircle2 className="h-5 w-5" />
                    <span className="font-medium">Completed</span>
                  </div>
                ) : (
                  <Button onClick={handleComplete} disabled={completeModuleMutation.isPending}>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    {completeModuleMutation.isPending ? 'Saving...' : 'Mark as Complete'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Render direct content reading experience
  return (
    <div className="min-h-screen bg-background print:bg-white">
      <div className="max-w-4xl mx-auto px-6 py-8 print:px-0 print:py-0">
        {/* Header - hide on print */}
        <div className="flex items-center justify-between mb-6 print:hidden">
          <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Pathway
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Module {module.sequence_order || 1}
            </span>
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <Card className="print:shadow-none print:border-none">
          <CardHeader className="print:pb-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2 print:hidden">
              <Badge variant="outline">Reading Material</Badge>
              <span>•</span>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {estimatedTime} min read
              </span>
              {totalPages > 1 && (
                <>
                  <span>•</span>
                  <span>Page {currentPage} of {totalPages}</span>
                </>
              )}
            </div>
            <CardTitle className="text-2xl print:text-3xl">{module.title}</CardTitle>
            {module.description && (
              <p className="text-muted-foreground mt-2 print:text-gray-600">{module.description}</p>
            )}
          </CardHeader>
          
          <CardContent>
            {/* Progress bar for multi-page content */}
            {totalPages > 1 && (
              <div className="mb-6 print:hidden">
                <Progress value={(currentPage / totalPages) * 100} className="h-2" />
              </div>
            )}

            {/* Content area */}
            <div 
              className="prose prose-sm sm:prose max-w-none dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: pages[currentPage - 1] || '' }}
            />

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-8 pt-6 border-t print:hidden">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            )}

            {/* Completion section */}
            <div className="mt-8 pt-6 border-t print:hidden">
              {isCompleted && completionData ? (
                <ModuleCompletionCard
                  completedModuleTitle={module?.title || 'Reading'}
                  nextModule={completionData.nextModule}
                  pathwayId={completionData.targetPathwayId || pathwayId || ''}
                  modulesCompleted={completionData.modulesCompleted}
                  totalModules={completionData.totalModules}
                  isPathwayComplete={completionData.isPathwayComplete}
                />
              ) : isCompleted ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle2 className="h-5 w-5" />
                    <span className="font-medium">Reading Completed</span>
                  </div>
                  <Button onClick={handleContinue}>
                    Continue Learning
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <p className="text-sm text-muted-foreground">
                    Finished reading? Mark this module as complete to continue your learning journey.
                  </p>
                  <Button
                    onClick={handleComplete}
                    disabled={completeModuleMutation.isPending}
                    className="gap-2"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    {completeModuleMutation.isPending ? 'Saving...' : 'Mark as Complete'}
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
